/**
 * Single source of truth for core public marketing page metadata.
 *
 * Consumed by:
 *  - <PublicPageSeo> (client-side Helmet: title, description, canonical, og:*, twitter:*)
 *  - the build-time `tm-static-seo` Vite plugin, which writes dist/<route>/index.html
 *    with this metadata baked into the static <head>, so non-JS crawlers and
 *    social-preview bots no longer see the sitewide default on every route.
 *
 * Keep this file dependency-free (it is imported by vite.config.ts).
 */
export const SITE_URL = "https://www.transportmate.co.uk";

export interface PublicPageMeta {
  path: string;
  title: string;
  description: string;
}

export const PUBLIC_PAGE_META: Record<string, PublicPageMeta> = {
  "/": {
    path: "/",
    title: "Vehicle Transport Management Software for UK Logistics",
    description:
      "UK vehicle transport software to manage jobs, drivers and invoices. Capture AI damage reports with GPS proof. Start your 30-day free trial.",
  },
  "/welcome": {
    path: "/welcome",
    title: "TransportMate — Vehicle Transport Software for UK Operators",
    description:
      "Manage UK vehicle transport jobs, drivers, damage reports and invoicing in one platform. Track deliveries with GPS proof. Start your free trial.",
  },
  "/features": {
    path: "/features",
    title: "Features — TransportMate Vehicle Transport Software",
    description:
      "Vehicle transport management software for UK logistics. Track drivers, capture digital appraisals, automate invoicing and manage proof of delivery.",
  },
  "/solutions": {
    path: "/solutions",
    title: "Solutions — TransportMate for UK Transport Operators",
    description:
      "Vehicle transport software for UK logistics teams. Schedule jobs, track drivers, manage subcontractors, automate PODs and invoicing. Start free.",
  },
  "/solutions/vehicle-transport-software": {
    path: "/solutions/vehicle-transport-software",
    title: "Car & Vehicle Transport Management Software UK | TransportMate",
    description:
      "Transport management software built for UK car and vehicle transport, not freight: jobs, driver allocation, inspections, PoC/PoD, tracking and invoicing.",
  },
  "/pricing": {
    path: "/pricing",
    title: "Transport Management Software Pricing Plans From £49/month",
    description:
      "UK transport management software with GPS tracking, digital appraisals, driver tools, automated invoicing and integrations. Start your free trial.",
  },
  "/industries": {
    path: "/industries",
    title: "UK Vehicle Logistics Software for Every Transport Operation",
    description:
      "Manage UK vehicle logistics with live job tracking, driver dispatch, appraisals, proof of delivery and automated invoicing. Start your free trial.",
  },
  "/resources": {
    path: "/resources",
    title: "Resources — Guides, FAQs and Support | TransportMate",
    description:
      "Explore TransportMate resources, FAQs and support for jobs, appraisals, invoicing, drivers, integrations and data. Get answers today.",
  },
  "/about": {
    path: "/about",
    title: "About TransportMate — Built by Transport Operators",
    description:
      "UK vehicle transport software to manage jobs, drivers, tracking, damage photos, PODs and invoicing. Start your 30-day free trial today.",
  },
  "/contact": {
    path: "/contact",
    title: "Contact TransportMate | Pricing, Onboarding & Support",
    description:
      "Contact TransportMate about UK transport management software, pricing and onboarding, or start your 30-day free trial today.",
  },
  "/start": {
    path: "/start",
    title: "30-Day Free Trial for UK Vehicle Transport Software",
    description:
      "TransportMate helps UK vehicle transport companies track drivers, capture damage, manage jobs and automate invoicing. Start a 30-day free trial.",
  },
  "/free-trial": {
    path: "/free-trial",
    title: "Transport Management Software | 30-Day Free Trial",
    description:
      "TransportMate helps UK vehicle transport companies track drivers, capture damage, manage appraisals and automate invoices. Start a 30-day free trial.",
  },
  "/privacy-policy": {
    path: "/privacy-policy",
    title: "TransportMate Privacy Policy | UK GDPR & Data Protection",
    description:
      "Read TransportMate’s Privacy Policy to learn how UK GDPR-compliant data collection, GPS tracking, AI tools, payments and user rights are handled.",
  },
  "/terms": {
    path: "/terms",
    title: "Transport Management Software Terms of Service | Legal Terms",
    description:
      "TransportMate’s Terms of Service covering use of the vehicle transport management platform and related services.",
  },
  "/delete-account": {
    path: "/delete-account",
    title: "TransportMate Account Deletion Request & Data Removal",
    description:
      "Request permanent TransportMate account deletion by email. Learn what data is deleted, retention exceptions, and the seven-day processing time.",
  },
};

export function canonicalFor(path: string): string {
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}
